import { action, makeObservable } from "mobx";
import { View } from "tamagui";
import { TKey } from "../../../../shared/translations/translationKeys";
import { ActivityEventType } from "../../../activities/types";
import type { Api } from "../../../core/apis";
import type { Stores } from "../../../core/stores";
import BaseModuleDataModel, {
  type BaseModuleModelData,
} from "../../core/BaseModuleDataModel";

export enum RsvpStatus {
  Coming = "coming",
  Maybe = "maybe",
  No = "no",
  NoAnswerYet = "no_answer_yet",
}

export interface RsvpVoteModelData extends BaseModuleModelData {
  userId: string;
  rsvp: RsvpStatus;
  lastChange: number;
  comment: string | null;
}

export default class RsvpUserModel extends BaseModuleDataModel<RsvpVoteModelData> {
  constructor(stores: Stores, api: Api, data: RsvpVoteModelData) {
    super(stores, api, data);

    makeObservable(this);
  }

  @action async setRsvp(option: RsvpStatus) {
    const oldOption = this.data.rsvp;

    if (oldOption !== option) {
      this.data.rsvp = option;
      this.data.lastChange = Date.now();

      this.updateTimestamp();
      await this.api.modulesDataApi.set(this);

      this.module.updateSnapshotWithoutSave();
      await this.module.save();

      let translatedOption = "";
      switch (option) {
        case RsvpStatus.No:
          translatedOption = "Not this time";
          break;
        case RsvpStatus.Maybe:
          translatedOption = "Maybe";
          break;
        case RsvpStatus.Coming:
          translatedOption = "Coming!";
          break;
      }

      this.module.postActivityEvent(
        ActivityEventType.DataChanged,
        TKey.ActivityRsvpUpdated,
        {
          newValue: translatedOption,
        },
      );
    }
  }

  @action setComment(comment: string | null) {
    const oldOption = this.data.comment;

    if (oldOption !== comment) {
      this.data.comment = comment;
      this.updateTimestamp();
      this.api.modulesDataApi.set(this);

      this.module.updateSnapshotWithoutSave();
      this.module.save();
    }
  }

  toFireStore(): Omit<RsvpVoteModelData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...super.toFireStore(),
      ...this.data,
    };
  }

  render() {
    return <View />;
  }

  static is(
    module: BaseModuleDataModel<BaseModuleModelData>,
  ): module is RsvpUserModel {
    return module instanceof RsvpUserModel;
  }
}
